import { useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { useDeleteUser } from '@/features/users';
import { getMessage } from '@/shared/copy/messages';
import { useCopyNotifications } from '@/shared/copy/notifications';
import { ApiError } from '@/shared/data/client';

export type DeleteAccountPanelState = {
  deleteOpen: boolean;
  deletePassword: string;
  deletePasswordError: string | null;
  deleteDisabled: boolean;
  confirmDisabled: boolean;
  isDeleting: boolean;
  onOpen: () => void;
  onClose: () => void;
  onPasswordChange: (value: string) => void;
  onConfirm: () => void;
};

export const useDeleteAccountPanel = (): DeleteAccountPanelState => {
  const { session: authSession, signOut } = useAuth();
  const deleteUser = useDeleteUser();
  const { notifyInfo } = useCopyNotifications();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deletePasswordError, setDeletePasswordError] = useState<string | null>(null);

  const deleteMatches = deletePassword.trim().length > 0;
  const deleteDisabled = deleteUser.isPending || !authSession;
  const confirmDisabled = !deleteMatches || !authSession;

  const handleOpenDelete = (): void => {
    if (!authSession) return;
    setDeletePasswordError(null);
    setDeletePassword('');
    setDeleteOpen(true);
  };

  const handleCloseDelete = (): void => {
    setDeleteOpen(false);
    setDeletePasswordError(null);
  };

  const handleConfirmDelete = (): void => {
    if (!authSession || !deleteMatches) return;
    deleteUser.mutate(
      { id: authSession.userId, password: deletePassword },
      {
        onSuccess: () => {
          setDeleteOpen(false);
          notifyInfo('account.delete.deleted');
          signOut();
        },
        onError: (err) => {
          const apiError = err instanceof ApiError ? err : null;
          const message = apiError?.message.toLowerCase() ?? '';
          if (
            apiError?.code === 'FORBIDDEN' &&
            (message.includes('password is incorrect') || message.includes('incorrect password'))
          ) {
            setDeletePasswordError(getMessage('account.delete.incorrectPassword'));
            return;
          }
          setDeletePasswordError(getMessage('account.delete.failed'));
        },
      },
    );
  };

  return {
    deleteOpen,
    deletePassword,
    deletePasswordError,
    deleteDisabled,
    confirmDisabled,
    isDeleting: deleteUser.isPending,
    onOpen: handleOpenDelete,
    onClose: handleCloseDelete,
    onPasswordChange: (value) => {
      setDeletePassword(value);
      setDeletePasswordError(null);
    },
    onConfirm: handleConfirmDelete,
  };
};
